/**
 * RoadmapsEditor Component
 *
 * Entity-specific editor for Roadmaps lookup table
 */

import {
  useRoadmaps,
  useCreateRoadmap,
  useUpdateRoadmap,
  useDeleteRoadmap,
  useCompanies,
} from '@medsuite/api-client';
import { Button, Alert, Spinner, useDialog } from '@medsuite/ui-components';
import { useState, useEffect } from 'react';

interface Roadmap {
  roadmap_id: number;
  company_id: number;
  company_name: string;
  roadmap_title: string;
  roadmap_context: string | null;
}

interface RoadmapsEditorProps {
  onDataLoaded?: (count: number) => void;
}

export default function RoadmapsEditor({ onDataLoaded }: RoadmapsEditorProps) {
  const { data: roadmaps = [], isLoading, error } = useRoadmaps();
  const { data: companies = [] } = useCompanies();
  const addMutation = useCreateRoadmap();
  const updateMutation = useUpdateRoadmap();
  const deleteMutation = useDeleteRoadmap();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({ company_id: 0, roadmap_title: '', roadmap_context: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<Roadmap>>({});

  // Dialog hooks
  const { confirm } = useDialog();

  // Sort alphabetically by title
  const sortedData = [...roadmaps].sort((a, b) => a.roadmap_title.localeCompare(b.roadmap_title));

  // Notify parent of data count
  useEffect(() => {
    if (onDataLoaded && roadmaps) {
      onDataLoaded(roadmaps.length);
    }
  }, [roadmaps, onDataLoaded]);

  // Set default company when companies load
  useEffect(() => {
    if (companies.length > 0 && newItem.company_id === 0) {
      setNewItem((prev) => ({ ...prev, company_id: companies[0].company_id }));
    }
  }, [companies, newItem.company_id]);

  // Handle add
  const handleSaveAdd = async () => {
    try {
      await addMutation.mutateAsync({
        company_id: newItem.company_id,
        roadmap_title: newItem.roadmap_title,
        roadmap_context: newItem.roadmap_context || null,
      });
      setIsAddingNew(false);
      setNewItem({
        company_id: companies[0]?.company_id || 0,
        roadmap_title: '',
        roadmap_context: '',
      });
    } catch (err) {
      console.error('Failed to add:', err);
    }
  };

  // Handle edit
  const handleSaveEdit = async () => {
    if (!editingId) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        data: {
          company_id: editingItem.company_id,
          roadmap_title: editingItem.roadmap_title,
          roadmap_context: editingItem.roadmap_context,
        },
      });
      setEditingId(null);
      setEditingItem({});
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: 'Delete Roadmap',
      message: 'Are you sure you want to delete this roadmap?',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load roadmaps: {error.message}</Alert>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {!isAddingNew && (
          <Button variant="primary" onClick={() => setIsAddingNew(true)}>
            Add New
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Context
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Add New Row */}
            {isAddingNew && (
              <tr className="bg-blue-50 dark:bg-blue-950">
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Auto-generated
                  </span>
                </td>
                <td className="px-6 py-4">
                  <select
                    value={newItem.company_id}
                    onChange={(e) =>
                      setNewItem({ ...newItem, company_id: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  >
                    {companies.map((company) => (
                      <option key={company.company_id} value={company.company_id}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="text"
                    value={newItem.roadmap_title}
                    onChange={(e) => setNewItem({ ...newItem, roadmap_title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                    placeholder="Roadmap title"
                  />
                </td>
                <td className="px-6 py-4">
                  <input
                    type="text"
                    value={newItem.roadmap_context}
                    onChange={(e) => setNewItem({ ...newItem, roadmap_context: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                    placeholder="Optional context"
                  />
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mr-3"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAdd}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                  >
                    Save
                  </button>
                </td>
              </tr>
            )}

            {/* Data Rows */}
            {sortedData.map((item: Roadmap) => {
              const isEditing = editingId === item.roadmap_id;
              return (
                <tr
                  key={item.roadmap_id}
                  className={isEditing ? 'bg-yellow-50 dark:bg-yellow-950' : ''}
                >
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {item.roadmap_id}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <select
                        value={editingItem.company_id || ''}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, company_id: parseInt(e.target.value) })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      >
                        {companies.map((company) => (
                          <option key={company.company_id} value={company.company_id}>
                            {company.company_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {item.company_name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingItem.roadmap_title || ''}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, roadmap_title: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      />
                    ) : (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {item.roadmap_title}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingItem.roadmap_context || ''}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, roadmap_context: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      />
                    ) : (
                      <div
                        className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                        title={item.roadmap_context || ''}
                      >
                        {item.roadmap_context || '—'}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mr-3"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(item.roadmap_id);
                            setEditingItem(item);
                          }}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.roadmap_id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
